import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function isSameDay(a, b) {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

// Monday-first offset of a month's first day (Date.getDay() is Sunday-first).
function leadingBlanks(year, month) {
  return (new Date(year, month, 1).getDay() + 6) % 7
}

const Wrap = styled.div`
  position: relative;
  display: inline-block;
`

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  background: ${({ theme }) => theme.bg1};
  color: ${({ theme, empty }) => (empty ? theme.text3 : theme.text1)};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  padding: 0.35rem 0.5rem;
  font-size: 0.85rem;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.text3};
  }
`

const Panel = styled.div`
  position: absolute;
  z-index: 50;
  top: calc(100% + 6px);
  left: 0;
  width: 264px;
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 0.75rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
`

const NavButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text2};
  font-size: 1rem;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;

  &:hover {
    background: ${({ theme }) => theme.bg2};
  }
`

const HeaderSelects = styled.div`
  display: flex;
  gap: 0.35rem;
`

const HeaderSelect = styled.select`
  cursor: pointer;
  background: ${({ theme }) => theme.bg2};
  color: ${({ theme }) => theme.text1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 6px;
  padding: 0.15rem 0.3rem;
  font-size: 0.8rem;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`

const WeekdayCell = styled.div`
  text-align: center;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.text3};
  padding: 0.2rem 0;
`

const DayCell = styled.button`
  cursor: pointer;
  text-align: center;
  font-size: 0.8rem;
  padding: 0.35rem 0;
  border-radius: 6px;
  border: none;
  background: ${({ theme, selected }) => (selected ? theme.primary1 ?? '#2172E5' : 'transparent')};
  color: ${({ theme, selected }) => (selected ? '#FFFFFF' : theme.text1)};

  &:hover {
    background: ${({ theme, selected }) => (selected ? theme.primary1 ?? '#2172E5' : theme.bg2)};
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.6rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.bg3};
`

const FooterLink = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  color: ${({ theme }) => theme.primary1 ?? '#2172E5'};
  font-size: 0.8rem;
  padding: 0;
`

function formatTrigger(date) {
  if (!date) return null
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Minimal single-day calendar picker: month/year dropdowns + prev/next, Mo-first grid, Clear/Today.
export default function DatePicker({ value, onChange, placeholder = 'Select date' }) {
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState((value ?? new Date()).getFullYear())
  const [viewMonth, setViewMonth] = useState((value ?? new Date()).getMonth())
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onEscape = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [open])

  const openPanel = () => {
    const base = value ?? new Date()
    setViewYear(base.getFullYear())
    setViewMonth(base.getMonth())
    setOpen(true)
  }

  const pick = (day) => {
    onChange(new Date(viewYear, viewMonth, day))
    setOpen(false)
  }

  const changeMonth = (delta) => {
    let m = viewMonth + delta
    let y = viewYear
    if (m < 0) {
      m = 11
      y -= 1
    } else if (m > 11) {
      m = 0
      y += 1
    }
    setViewMonth(m)
    setViewYear(y)
  }

  const blanks = leadingBlanks(viewYear, viewMonth)
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const years = Array.from({ length: 21 }, (_, i) => new Date().getFullYear() - 10 + i)

  return (
    <Wrap ref={wrapRef}>
      <Trigger type="button" empty={!value} onClick={() => (open ? setOpen(false) : openPanel())}>
        <span role="img" aria-label="Calendar">
          📅
        </span>{' '}
        {formatTrigger(value) ?? placeholder}
      </Trigger>
      {open && (
        <Panel>
          <Header>
            <NavButton type="button" onClick={() => changeMonth(-1)}>
              ‹
            </NavButton>
            <HeaderSelects>
              <HeaderSelect value={viewMonth} onChange={(e) => setViewMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </HeaderSelect>
              <HeaderSelect value={viewYear} onChange={(e) => setViewYear(Number(e.target.value))}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </HeaderSelect>
            </HeaderSelects>
            <NavButton type="button" onClick={() => changeMonth(1)}>
              ›
            </NavButton>
          </Header>
          <Grid>
            {WEEKDAYS.map((w) => (
              <WeekdayCell key={w}>{w}</WeekdayCell>
            ))}
            {Array.from({ length: blanks }).map((_, i) => (
              <div key={`b${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
              <DayCell
                key={day}
                type="button"
                selected={isSameDay(value, new Date(viewYear, viewMonth, day))}
                onClick={() => pick(day)}
              >
                {day}
              </DayCell>
            ))}
          </Grid>
          <Footer>
            <FooterLink
              type="button"
              onClick={() => {
                onChange(null)
                setOpen(false)
              }}
            >
              Clear
            </FooterLink>
            <FooterLink
              type="button"
              onClick={() => {
                onChange(new Date())
                setOpen(false)
              }}
            >
              Today
            </FooterLink>
          </Footer>
        </Panel>
      )}
    </Wrap>
  )
}
